// ============================================================================
// OBT F3 — Filtre-dışı park önerileri inceleme listesi (BM)
// ============================================================================
// Öneriyi yapan Formen'in ASLİ doğrudan BM'si onaylar/reddeder (backend fail-
// closed). Onay → park supplemental kapsama eklenir (öneren cevaplayabilir).
// Fotoğraflar ACL'li öneri-medyası ucundan blob okunur.
// ============================================================================

import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, XCircle, MapPin } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState, ObtStatusBadge } from '../ObtStates';
import { SUGGESTED_PARK_STATUS_LABELS } from './reviewEnums';
import { readSuggestionPhoto } from './reviewApi';
import { useSuggestedParkReviews } from './hooks/useSuggestedParkReviews';
import ReviewMediaGallery from './ReviewMediaGallery';
import SuggestedParkReviewDialog from './SuggestedParkReviewDialog';

/**
 * @param {object} props
 * @param {object} props.publication  {publication_id, campaign_title}
 * @param {Map<string,string>} [props.nameById]
 * @param {() => void} props.onBack
 * @param {() => void} [props.onChanged]
 */
export default function SuggestedParkReviewList({
  publication,
  nameById,
  onBack,
  onChanged,
}) {
  const publicationId = publication?.publication_id
    ? String(publication.publication_id)
    : null;
  const { loading, error, suggestions, reload, approve, reject, pendingId, actionError } =
    useSuggestedParkReviews(publicationId, { onChanged });

  const [dialog, setDialog] = useState({ mode: null, suggestion: null });

  const loadPhotoFor = useMemo(
    () => (suggestionId) => (photoId, opts) =>
      readSuggestionPhoto(suggestionId, photoId, opts),
    []
  );

  const closeDialog = () => setDialog({ mode: null, suggestion: null });

  const confirmDecision = async (comment) => {
    const { mode, suggestion } = dialog;
    if (!suggestion) return;
    if (mode === 'approve') await approve(String(suggestion.suggestion_id), comment);
    else await reject(String(suggestion.suggestion_id), comment);
    closeDialog();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> Geri
        </Button>
        <h3 className="truncate text-base font-semibold text-gray-900">
          Önerilen Parklar — {publication?.campaign_title || ''}
        </h3>
      </div>

      {loading && <ObtLoading label="Öneriler yükleniyor…" />}
      {!loading && error && <ObtErrorState error={error} onRetry={reload} />}
      {!loading && !error && suggestions.length === 0 && (
        <ObtEmptyState
          title="Bekleyen park önerisi yok"
          description="Bu yayında kararınızı bekleyen filtre-dışı park önerisi bulunmuyor."
        />
      )}

      {!loading && !error && suggestions.length > 0 && (
        <div className="space-y-3">
          {actionError && (
            <p role="alert" className="text-sm text-red-600">
              {actionError.message}
            </p>
          )}
          {suggestions.map((s) => {
            const sid = String(s.suggestion_id);
            const busy = pendingId === sid;
            return (
              <Card key={sid}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
                      <span className="text-sm font-semibold text-gray-900">
                        {s.park_name}
                      </span>
                      {s.region_name && (
                        <span className="text-xs text-gray-500">{s.region_name}</span>
                      )}
                    </div>
                    <ObtStatusBadge
                      value={s.status}
                      labelMap={SUGGESTED_PARK_STATUS_LABELS}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Öneren:{' '}
                    {nameById?.get(String(s.suggested_by_personnel_id)) ||
                      'Bilinmeyen personel'}
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                    {s.description}
                  </p>
                  <ReviewMediaGallery
                    photoIds={s.photo_ids}
                    loadPhoto={loadPhotoFor(sid)}
                    emptyText="Bu öneriye bağlı fotoğraf yok."
                    altPrefix="Öneri fotoğrafı"
                  />
                  {s.status === 'pending_bm_review' && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        disabled={busy}
                        onClick={() => setDialog({ mode: 'approve', suggestion: s })}
                      >
                        <CheckCircle2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                        Onayla
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={busy}
                        onClick={() => setDialog({ mode: 'reject', suggestion: s })}
                      >
                        <XCircle className="mr-1.5 h-4 w-4" aria-hidden="true" /> Reddet
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SuggestedParkReviewDialog
        mode={dialog.mode}
        suggestion={dialog.suggestion}
        onClose={closeDialog}
        onConfirm={confirmDecision}
        error={actionError?.message || null}
      />
    </div>
  );
}
